import React, { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { Box } from '@mui/material';
import { DetailCard } from './DetailCard';
import { RiderDetails } from './RiderDetails';
import { TenantDetails } from './TenantDetails';
import { useDispatch, useSelector } from 'react-redux';
import { openDetails } from 'store/actions/uiActions';
import { getAppDetailAction } from 'store/actions/propertyActions';
import { useHydrateDetailsOnce } from 'hooks/useHydrateDetailsOnce';
import { maskDemoField } from 'utils/demoMask';
import { DetailPageShell, SectionPanelSkeleton } from 'components/DetailPageState';
import { useDetailPageState } from 'hooks/useDetailPageState';
import { useImmutableJs } from 'hooks/useImmutableJs';

export const ApplicationDetail = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { applicationId } = useParams();

  const isDemo = useSelector((state) => state.getIn(['ui', 'demo'])) || false;
  const details = useSelector((state) => state.getIn(['ui', 'details'])) || null;
  const detailsJSON = useImmutableJs(details);
  const applicationDetail =
    useSelector((state) => state.getIn(['property', 'application-detail'])) || null;
  const applicationDetailJSON = useImmutableJs(applicationDetail);
  const applicationFromApi = applicationDetailJSON?.data || null;
  const hasUiFallback = !applicationId && detailsJSON?.type === 'application' && detailsJSON?.data;
  const {
    data: application,
    isLoading,
    hasError,
  } = useDetailPageState({
    routeId: applicationId,
    detailRecord: applicationDetailJSON,
    hasUiFallback,
    fallbackData: detailsJSON?.data || null,
  });
  const fromApplicationsList = Boolean(location.state?.fromApplicationsList);
  const fromCancellationLog = Boolean(location.state?.fromCancellationLog);

  const onClickBack = useCallback(() => {
    dispatch(openDetails(null));
    if ((fromApplicationsList || fromCancellationLog) && window.history.length > 1) {
      navigate(-1);
      return;
    }
    if (fromCancellationLog) {
      navigate('/cancellation-log');
      return;
    }
    navigate('/property/applications');
  }, [dispatch, navigate, fromApplicationsList, fromCancellationLog]);

  const onRetry = useCallback(() => {
    if (applicationId) dispatch(getAppDetailAction(applicationId));
  }, [applicationId, dispatch]);

  useEffect(() => {
    if (!applicationId) return;
    dispatch(getAppDetailAction(applicationId));
  }, [applicationId, dispatch]);

  useHydrateDetailsOnce({
    type: 'application',
    data: applicationFromApi,
    id: applicationFromApi?.app_id,
  });

  useEffect(() => {
    if (applicationId) return;
    const fallbackDetails = detailsJSON;
    if (!details || fallbackDetails.type !== 'application') {
      onClickBack();
    }
  }, [applicationId, details, detailsJSON, onClickBack]);

  return (
    <DashboardLayoutContainer shouldShowCard={false}>
      <DetailPageShell
        onBack={onClickBack}
        title={application ? maskDemoField(isDemo, 'tenant-name', application.name) : null}
        isLoading={isLoading}
        hasError={hasError}
        onRetry={onRetry}
        loadingLabel="Loading application details..."
        skeletonVariant="fields"
      >
        {application ? <DetailCard application={application} isDemo={isDemo} /> : null}
      </DetailPageShell>
      {isLoading ? (
        <>
          <SectionPanelSkeleton />
          <SectionPanelSkeleton />
        </>
      ) : hasError || !application ? null : (
        <>
          <Box borderRadius="12px" sx={{ border: '1px solid #EAEAEA', background: 'white' }} mt={1}>
            <RiderDetails application={application} isDemo={isDemo} />
          </Box>
          <Box borderRadius="12px" sx={{ border: '1px solid #EAEAEA', background: 'white' }} mt={1}>
            <TenantDetails application={application} isDemo={isDemo} />
          </Box>
        </>
      )}
    </DashboardLayoutContainer>
  );
};
